import { useCallback, useRef } from "react";
import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  runTransaction,
} from "firebase/firestore";
import { db } from "../../services/firebase.config";
import { useAuth } from "../../services/AuthContext";
import { useLoadingManager } from "../../services/LoadingManager";
import mixpanelService from "../../services/MixpanelService";
import postHogService from "../../services/PostHogService";

export const useFriendActions = ({
  loadFriendsAndRequests,
  invalidateFriendCaches,
}) => {
  const { currentUserState } = useAuth();
  const loadingManager = useLoadingManager();

  const currentUserRef = useRef(currentUserState);
  const loadingManagerRef = useRef(loadingManager);

  const sendFriendRequest = useCallback(
    async (friendId) => {
      const user = currentUserRef.current;
      const loading = loadingManagerRef.current;
      if (!user) return;
      try {
        loading.setLoading(`add-friend-${friendId}`);
        await runTransaction(db, async (transaction) => {
          const userRef = doc(db, "public-profiles", user.uid);
          const friendRef = doc(db, "public-profiles", friendId);
          const userDoc = await transaction.get(userRef);
          const friendDoc = await transaction.get(friendRef);
          if (!userDoc.exists()) throw new Error("Your profile not found");
          if (!friendDoc.exists()) throw new Error("Friend profile not found");
          const userSent = userDoc.data().friendRequests?.sent || [];
          const friendReceived =
            friendDoc.data().friendRequests?.received || [];
          if (userSent.includes(friendId))
            throw new Error("Friend request already sent");
          if (friendReceived.includes(user.uid))
            throw new Error("Friend request already exists");
          transaction.update(userRef, {
            "friendRequests.sent": arrayUnion(friendId),
          });
          transaction.update(friendRef, {
            "friendRequests.received": arrayUnion(user.uid),
          });
        });
        invalidateFriendCaches(user.uid);
        invalidateFriendCaches(friendId);
        await loadFriendsAndRequests();
        loading.setSuccess(`add-friend-${friendId}`);

        // Track friend request sent
        const friendRequestSentProps = {
          friend_id: friendId,
          user_id: user.uid,
          source: "friends_page",
          timestamp: new Date().toISOString(),
        };
        mixpanelService.track("Friend Request Sent", friendRequestSentProps);
        postHogService.track("Friend Request Sent", friendRequestSentProps);
      } catch (error) {
        loading.setError(`add-friend-${friendId}`, error);
        if (error.message.includes("already"))
          alert("This friend request already exists!");
        else alert(`Failed to send friend request: ${error.message}`);
      }
    },
    [invalidateFriendCaches, loadFriendsAndRequests]
  );

  const acceptFriendRequest = useCallback(
    async (requesterId) => {
      const user = currentUserRef.current;
      const loading = loadingManagerRef.current;
      if (!user) return;
      try {
        loading.setLoading(`accept-request-${requesterId}`);
        const userRef = doc(db, "public-profiles", user.uid);
        const requesterRef = doc(db, "public-profiles", requesterId);
        await runTransaction(db, async (transaction) => {
          const userDocTx = await transaction.get(userRef);
          const requesterDocTx = await transaction.get(requesterRef);
          if (!userDocTx.exists()) throw new Error("Your profile not found");
          if (!requesterDocTx.exists())
            throw new Error("Requester profile not found");
          const userReceived = userDocTx.data().friendRequests?.received || [];
          const requesterSent =
            requesterDocTx.data().friendRequests?.sent || [];
          if (!userReceived.includes(requesterId))
            throw new Error(
              "Friend request not found in your received requests"
            );
          if (!requesterSent.includes(user.uid))
            throw new Error(
              "Friend request not found in sender's sent requests"
            );
          transaction.update(userRef, {
            friends: arrayUnion(requesterId),
            "friendRequests.received": arrayRemove(requesterId),
          });
          transaction.update(requesterRef, {
            friends: arrayUnion(user.uid),
            "friendRequests.sent": arrayRemove(user.uid),
          });
        });
        invalidateFriendCaches(user.uid);
        invalidateFriendCaches(requesterId);
        await loadFriendsAndRequests();
        loading.setSuccess(`accept-request-${requesterId}`);

        // Track friend request accepted
        const friendRequestAcceptedProps = {
          friend_id: requesterId,
          user_id: user.uid,
          timestamp: new Date().toISOString(),
        };
        mixpanelService.track(
          "Friend Request Accepted",
          friendRequestAcceptedProps
        );
        postHogService.track(
          "Friend Request Accepted",
          friendRequestAcceptedProps
        );
      } catch (error) {
        loading.setError(`accept-request-${requesterId}`, error);
        alert(`Failed to accept friend request: ${error.message}`);
      }
    },
    [invalidateFriendCaches, loadFriendsAndRequests]
  );

  const declineFriendRequest = useCallback(
    async (requesterId) => {
      const user = currentUserRef.current;
      const loading = loadingManagerRef.current;
      if (!user) return;
      try {
        loading.setLoading(`decline-request-${requesterId}`);
        const userRef = doc(db, "public-profiles", user.uid);
        const requesterRef = doc(db, "public-profiles", requesterId);
        await Promise.all([
          updateDoc(userRef, {
            "friendRequests.received": arrayRemove(requesterId),
          }),
          updateDoc(requesterRef, {
            "friendRequests.sent": arrayRemove(user.uid),
          }),
        ]);
        invalidateFriendCaches(user.uid);
        invalidateFriendCaches(requesterId);
        await loadFriendsAndRequests();
        loading.setSuccess(`decline-request-${requesterId}`);
      } catch (error) {
        loading.setError(`decline-request-${requesterId}`, error);
      }
    },
    [invalidateFriendCaches, loadFriendsAndRequests]
  );

  const cancelFriendRequest = useCallback(
    async (recipientId) => {
      const user = currentUserRef.current;
      const loading = loadingManagerRef.current;
      if (!user) return;
      try {
        loading.setLoading(`cancel-request-${recipientId}`);
        const userRef = doc(db, "public-profiles", user.uid);
        const recipientRef = doc(db, "public-profiles", recipientId);
        await Promise.all([
          updateDoc(userRef, {
            "friendRequests.sent": arrayRemove(recipientId),
          }),
          updateDoc(recipientRef, {
            "friendRequests.received": arrayRemove(user.uid),
          }),
        ]);
        invalidateFriendCaches(user.uid);
        invalidateFriendCaches(recipientId);
        await loadFriendsAndRequests();
        loading.setSuccess(`cancel-request-${recipientId}`);
      } catch (error) {
        loading.setError(`cancel-request-${recipientId}`, error);
      }
    },
    [invalidateFriendCaches, loadFriendsAndRequests]
  );

  const removeFriend = useCallback(
    async (friendId) => {
      const user = currentUserRef.current;
      const loading = loadingManagerRef.current;
      if (!user) return;
      try {
        loading.setLoading(`remove-friend-${friendId}`);
        const userRef = doc(db, "public-profiles", user.uid);
        const friendRef = doc(db, "public-profiles", friendId);
        await Promise.all([
          updateDoc(userRef, { friends: arrayRemove(friendId) }),
          updateDoc(friendRef, { friends: arrayRemove(user.uid) }),
        ]);
        invalidateFriendCaches(user.uid);
        invalidateFriendCaches(friendId);
        await loadFriendsAndRequests();
        loading.setSuccess(`remove-friend-${friendId}`);
      } catch (error) {
        loading.setError(`remove-friend-${friendId}`, error);
      }
    },
    [invalidateFriendCaches, loadFriendsAndRequests]
  );

  return {
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    cancelFriendRequest,
    removeFriend,
  };
};
